import {
  PayloadAction,
  createSlice,
  createEntityAdapter,
  EntityState,
} from '@reduxjs/toolkit';

interface InProgressLro<TState> {
  id: string;
  type: string;
  status: 'inprogress';
  state: TState;
  startTime: number;
}
type FaultedLro<TState> = Omit<InProgressLro<TState>, 'status'> & {
  status: 'faulted';
  faultTime: number;
  faultCode: string;
};
type CompletedLro<TState> = Omit<InProgressLro<TState>, 'status'> & {
  status: 'completed';
  completionTime: number;
};

export type LongRunningOperation<TState = unknown> =
  | InProgressLro<TState>
  | FaultedLro<TState>
  | CompletedLro<TState>;

const lroAdapter = createEntityAdapter({
  selectId: (lro: LongRunningOperation) => lro.id,
});
const lroSelectors = lroAdapter.getSelectors();

export const lroSlice = createSlice({
  name: 'lro',
  initialState: lroAdapter.getInitialState(),
  reducers: {
    lroCreated(
      state,
      action: PayloadAction<{ id: string; type: string; state: unknown }>
    ) {
      const lro: LongRunningOperation = {
        ...action.payload,
        status: 'inprogress',
        startTime: Date.now(),
      };
      lroAdapter.addOne(state, lro);
    },
    lroUpdated(state, action: PayloadAction<{ id: string; state: unknown }>) {
      lroAdapter.updateOne(state, {
        id: action.payload.id,
        changes: { state: action.payload.state },
      });
    },
    lroFaulted(
      state,
      action: PayloadAction<{ id: string; faultCode: string; state?: unknown }>
    ) {
      lroAdapter.updateOne(state, {
        id: action.payload.id,
        changes: {
          ...action.payload,
          status: 'faulted',
          faultTime: Date.now(),
        },
      });
    },
    lroCompleted(state, action: PayloadAction<{ id: string; state: unknown }>) {
      lroAdapter.updateOne(state, {
        id: action.payload.id,
        changes: {
          ...action.payload,
          status: 'completed',
          completionTime: Date.now(),
        },
      });
    },
  },
  selectors: {
    selectById: lroSelectors.selectById,
    selectAllByStatus(
      state: EntityState<LongRunningOperation, string>,
      status: LongRunningOperation['status']
    ) {
      return lroSelectors
        .selectAll(state)
        .filter((lro) => lro.status === status);
    },
  },
});
